import type { GameConfig, ResourceAmount } from '../types.js';

export type BalanceListener = (resourceId: string, value: number) => void;

function aggregateCost(cost: ResourceAmount[]): Map<string, number> | null {
    const totals = new Map<string, number>();
    for (const { resource, quantity } of cost) {
        const amount = quantity ?? 0;
        if (!resource || !Number.isFinite(amount) || amount < 0) return null;
        totals.set(resource, (totals.get(resource) ?? 0) + amount);
    }
    return totals;
}

/** Player balances and atomic resource transactions. */
export class Economy {
    private readonly balances: Map<string, number>;
    private readonly resourceIds: Set<string>;
    private readonly onChange: BalanceListener | null;

    constructor(config: GameConfig, onChange: BalanceListener | null = null) {
        this.onChange = onChange;
        this.resourceIds = new Set((config.resources ?? []).map((resource) => resource.id));
        this.balances = new Map(
            (config.resources ?? []).map((resource) => [resource.id, resource.initial ?? 0]),
        );
    }

    get(resourceId: string): number {
        return this.balances.get(resourceId) ?? 0;
    }

    canAfford(cost: ResourceAmount[] = []): boolean {
        const totals = aggregateCost(cost);
        return totals !== null && [...totals].every(([resource, quantity]) => this.get(resource) >= quantity);
    }

    spend(cost: ResourceAmount[] = []): boolean {
        const totals = aggregateCost(cost);
        if (totals === null || !this.canAfford(cost)) return false;

        totals.forEach((quantity, resource) => {
            this.setValue(resource, this.get(resource) - quantity, false);
        });
        totals.forEach((_, resource) => {
            this.onChange?.(resource, this.get(resource));
        });
        return true;
    }

    add(resourceId: string, quantity: number): void {
        this.set(resourceId, this.get(resourceId) + quantity);
    }

    set(resourceId: string, value: number): void {
        this.setValue(resourceId, value, true);
    }

    private setValue(resourceId: string, value: number, notify: boolean): void {
        if (!this.resourceIds.has(resourceId)) return;
        const next = Math.max(0, Math.round(value));
        this.balances.set(resourceId, next);
        if (notify) this.onChange?.(resourceId, next);
    }

    snapshot(): Record<string, number> {
        return Object.fromEntries(this.balances);
    }

    restore(saved: Record<string, number> | null | undefined): void {
        if (!saved) return;

        Object.entries(saved).forEach(([id, value]) => {
            if (typeof value === 'number' && Number.isFinite(value)) this.set(id, value);
        });
    }
}
